import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import OfferTable from '../EvaluationOperational/Components/OfferTable';
import TenderNotClosedAlert from '../EvaluationOperational/Components/TenderNotClosedAlert';
import {
  canAccessEvaluationOperationalData,
  getEvaluationOperationalLockReason,
} from '../../../utils/tenderEvaluationHelpers';
import {
  formatImprovementClosingDate,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../../shared/offer-domain/improvement';

const Improvement = ({
  procurement,
  procId,
  parcels = [],
  isLoadingParcels = false,
  isLoadingProcurement = false,
  closingDate: closingDateProp = null,
  onImprovementClosingDateChange,
  isActive = true,
}) => {
  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [isImprovementReady, setIsImprovementReady] = useState(false);

  const handleImprovementMeta = useCallback(
    ({ deliveryDetails: rows, ready = false } = {}) => {
      if (!ready) return;
      const nextRows = Array.isArray(rows) ? rows : [];
      setDeliveryDetails(nextRows);
      setIsImprovementReady(true);
      const date = resolveImprovementClosingDate(nextRows);
      onImprovementClosingDateChange?.(date);
    },
    [onImprovementClosingDateChange]
  );

  const closingDate = useMemo(
    () => resolveImprovementClosingDate(deliveryDetails) ?? closingDateProp,
    [deliveryDetails, closingDateProp]
  );
  const isImprovementOpen = isImprovementClosingActive(closingDate);
  const showAwaitingGate =
    isImprovementReady && Boolean(closingDate) && isImprovementOpen;

  if (isLoadingProcurement && !procurement) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted mt-2'>Loading improvement data...</p>
      </div>
    );
  }

  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessData = canAccessEvaluationOperationalData(procurement);

  if (!canAccessData) {
    return (
      <TenderNotClosedAlert featureLabel='Improvement' reason={lockReason} />
    );
  }

  if (showAwaitingGate) {
    return (
      <TenderNotClosedAlert featureLabel='Improvement' reason='awaiting' />
    );
  }

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <div className='mb-4'>
          <h5 className='fw-bold text-dark mb-1'>Improvement Detail</h5>
          <p className='text-muted small mb-0'>
            List of offers that have been requested for improvement.
          </p>
          {isImprovementReady && closingDate ? (
            <Alert color='danger' className='mb-0 mt-3'>
              <h6 className='alert-heading mb-1 fw-semibold'>
                Improvement has ended
              </h6>
              <p className='mb-0 fs-13'>
                Improvement has ended. It ended on{' '}
                {formatImprovementClosingDate(closingDate)}.
              </p>
            </Alert>
          ) : null}
        </div>
        {!isImprovementReady ? (
          <div className='text-center py-4'>
            <Spinner size='sm' color='primary' />
            <p className='text-muted mt-2 mb-0'>Loading improvement data...</p>
          </div>
        ) : null}
        <Row className={isImprovementReady ? 'mt-3' : 'd-none'}>
          <Col md={12}>
            <OfferTable
              viewMode='improvement'
              procurementId={procurement?.id || procId}
              procurement={procurement}
              parcels={parcels}
              isLoadingParcels={isLoadingParcels}
              onImprovementMeta={handleImprovementMeta}
              isActive={isActive}
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default Improvement;
